import React, { useEffect, useState } from "react";
import { Box, useTheme } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import ViewCompanyEarningsLoader from "../Charts/ViewCompanyEarningsLoader";
import HomeTab from "./tabs/HomeTab";
import SearchTab from "./tabs/SearchTab";
import CalendarTab from "./tabs/CalendarTab";
import { Theme } from "@mui/material/styles";
import { hapticSelection } from "../../../lib/haptics";
import { useTouchLayout } from "../../../hooks/useTouchLayout";
import { focusRingSx } from "../overviewPanel";
import { touchHitAreaSx } from "../EarningsCalendar/touchHitArea";

export type EarningsTab = "home" | "search" | "calendar";

const getFlatUIColors = (theme: Theme) => {
  const isDark = theme.palette.mode === "dark";

  return {
    background: isDark ? "#1a1a1a" : "#ffffff",
  };
};

// "Your watchlist" and "the whole market" are peers, not a drill-down, so they
// get equal billing in a pill switch. Search stays what it is — a drill-down
// from Home with its own back button — so the switch hides while it's open.
const SWITCH_TABS: { key: EarningsTab; label: string }[] = [
  { key: "home", label: "Watchlist" },
  { key: "calendar", label: "Calendar" },
];

const SWITCH_PILL_HEIGHT = 30;

interface EarningsPageProps {
  initialTab?: EarningsTab;
}

const EarningsPage: React.FC<EarningsPageProps> = ({ initialTab = "home" }) => {
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const isTouch = useTouchLayout();
  const isDark = theme.palette.mode === "dark";
  const flatUIColors = getFlatUIColors(theme);
  const [activeTab, setActiveTab] = useState<EarningsTab>(initialTab);

  const ticker = searchParams.get("ticker");

  const handleTabChange = (tab: EarningsTab) => {
    hapticSelection();
    setActiveTab(tab);
  };

  // Tabs swap content inside one route, so the document keeps whatever scroll
  // position the previous tab had. Arriving at Search already scrolled past its
  // search box — the one thing on the page — meant scrolling back up to use it.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeTab]);

  if (ticker) {
    return <ViewCompanyEarningsLoader />;
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case "search":
        return <SearchTab onBack={() => handleTabChange("home")} />;
      case "calendar":
        return <CalendarTab />;
      case "home":
      default:
        return <HomeTab onTabChange={handleTabChange} />;
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: flatUIColors.background,
        // dvh, not vh: under the native shell NativeTabLayout already reserves
        // the 56px tab bar plus its safe area, so 100vh overflows by exactly
        // that much and leaves the last row unreachable.
        minHeight: "100dvh",
        pb: 6,
      }}
    >
      {activeTab !== "search" && (
        <Box sx={{ px: { xs: 2, md: 3 }, pt: { xs: 2, md: 3 }, pb: 0 }}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.25,
              p: 0.25,
              backgroundColor: isDark ? "#212121" : "#f5f5f5",
              border: isDark ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.1)",
              borderRadius: 1,
            }}
          >
            {SWITCH_TABS.map((tab) => {
              const selected = activeTab === tab.key;
              return (
                <Box
                  key={tab.key}
                  component="button"
                  aria-pressed={selected}
                  onClick={() => {
                    if (selected) return;
                    handleTabChange(tab.key);
                  }}
                  sx={{
                    all: "unset",
                    ...focusRingSx,
                    cursor: "pointer",
                    fontFamily: "inherit",
                    fontSize: "0.75rem",
                    fontWeight: selected ? 600 : 500,
                    color: selected ? "#22c55e" : theme.palette.text.secondary,
                    px: 1.25,
                    height: SWITCH_PILL_HEIGHT,
                    display: "inline-flex",
                    alignItems: "center",
                    borderRadius: 0.75,
                    backgroundColor: selected
                      ? isDark
                        ? "rgba(34,197,94,0.08)"
                        : "rgba(34,197,94,0.06)"
                      : "transparent",
                    transition: "color 200ms linear, background-color 200ms linear",
                    WebkitTapHighlightColor: "transparent",
                    "&:hover": { color: selected ? "#22c55e" : theme.palette.text.primary },
                    ...(isTouch && touchHitAreaSx(SWITCH_PILL_HEIGHT)),
                  }}
                >
                  {tab.label}
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
      {renderTabContent()}
    </Box>
  );
};

export default EarningsPage;
